import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChange, isAdmin, hasAdminAccess as checkAdminAccess, getUserRole } from '../firebase/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: any;
  userRole: 'admin' | 'affiliate' | 'user';
  hasAdminAccess: boolean;
  setAuthenticated: (authenticated: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<'admin' | 'affiliate' | 'user'>('user');
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  useEffect(() => {
    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChange((user) => {
      if (user && isAdmin(user)) {
        const role = getUserRole(user);
        const adminAccess = checkAdminAccess(user);
        
        setIsAuthenticated(true);
        setUser(user);
        setUserRole(role);
        setHasAdminAccess(adminAccess);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setUserRole('user');
        setHasAdminAccess(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const setAuthenticated = (authenticated: boolean) => {
    setIsAuthenticated(authenticated);
  };

  const value = {
    isAuthenticated,
    loading,
    user,
    userRole,
    hasAdminAccess,
    setAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
